
import { useCallback, useMemo } from 'react';
import { ResearchLibraryItem } from '../library';
import { ResearchItem } from '@/components/research-generator';

interface UseResearchItemConverterProps {
  selectedResearchId: string | null;
  selectedResearchDetails: any;
  allResearch: ResearchLibraryItem[];
}

export const useResearchItemConverter = ({
  selectedResearchId,
  selectedResearchDetails,
  allResearch,
}: UseResearchItemConverterProps) => {
  // Convert ResearchLibraryItem to ResearchItem for lightbox compatibility
  const convertToResearchItem = useCallback((item: ResearchLibraryItem): ResearchItem => {
    return {
      id: item.id,
      title: item.title,
      content: item.content,
      researchType: item.research_type,
      createdAt: item.created_at,
    };
  }, []);

  // Create the selected research object for the lightbox
  const selectedResearch = useMemo((): ResearchItem | null => {
    if (!selectedResearchId) return null;
    
    // First try to get from details (this will have the most up-to-date data)
    if (selectedResearchDetails) {
      return {
        id: selectedResearchDetails.id,
        title: selectedResearchDetails.title,
        content: selectedResearchDetails.content,
        researchType: selectedResearchDetails.research_type,
        createdAt: selectedResearchDetails.created_at || new Date().toISOString(),
      };
    }
    
    // Fallback to cached data if available
    const cachedResearch = allResearch.find(r => r.id === selectedResearchId);
    if (cachedResearch) {
      return convertToResearchItem(cachedResearch);
    }
    
    return null;
  }, [selectedResearchId, selectedResearchDetails, allResearch, convertToResearchItem]);

  return {
    selectedResearch,
    convertToResearchItem,
  };
};
